import { Pause, Play } from 'lucide-react';
import { motion } from 'motion/react';
import { useCallback } from 'react';
import { useAudioPlayer } from '../hooks/useAudioPlayer';

interface Props {
  trackId: string;
  src: string;
  playingTrack: string | null;
  setPlayingTrack: (id: string | null) => void;
}

function formatTime(seconds: number): string {
  const safe = Number.isFinite(seconds) ? Math.floor(seconds) : 0;
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

const BARS = Array.from({ length: 40 });

export function AudioPlayer({
  trackId,
  src,
  playingTrack,
  setPlayingTrack,
}: Props) {
  const isPlaying = playingTrack === trackId;

  const onStop = useCallback(() => setPlayingTrack(null), [setPlayingTrack]);

  const {
    audioRef,
    currentTime,
    duration,
    progressPercent,
    seek,
    onLoadedMetadata,
    onTimeUpdate,
    onEnded,
  } = useAudioPlayer(src, isPlaying, onStop);

  const togglePlay = () => setPlayingTrack(isPlaying ? null : trackId);

  return (
    <div className="space-y-4">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={onLoadedMetadata}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
      />

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
          className="w-12 h-12 shrink-0 rounded-full bg-purple-600 hover:bg-purple-700 flex items-center justify-center text-white shadow-md transition-colors"
        >
          {isPlaying ? (
            <Pause className="w-6 h-6" />
          ) : (
            <Play className="w-6 h-6 ml-1" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <input
            type="range"
            min={0}
            max={duration}
            value={currentTime}
            onChange={(e) => seek(Number(e.target.value))}
            aria-label="Перемотка"
            className="w-full h-2 bg-gray-200 rounded-[10px] appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #9333ea ${progressPercent}%, #e5e7eb ${progressPercent}%)`,
            }}
          />
        </div>

        <span className="shrink-0 whitespace-nowrap text-sm leading-5 font-mono text-gray-700 tabular-nums">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>

      {isPlaying && (
        <div className="flex items-center justify-center gap-1 h-12">
          {BARS.map((_, i) => (
            <motion.div
              key={i}
              className="w-1 bg-purple-500 rounded-full"
              animate={{
                height: [
                  Math.random() * 30 + 10,
                  Math.random() * 40 + 5,
                  Math.random() * 35 + 10,
                ],
              }}
              transition={{
                duration: 0.5,
                repeat: Infinity,
                repeatType: 'reverse',
                delay: i * 0.05,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
